import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { ShowToastOptions, VideochamadaToastModel, VideochamadaToastPosition } from './model-videochamadaToast'

interface VideochamadaToastContextValue {
  toasts: VideochamadaToastModel[]
  show(options: ShowToastOptions): string
  dismiss(id: string): void
  clear(position?: VideochamadaToastPosition): void
}

interface VideochamadaToastProviderProps {
  children: ReactNode
}

const TOAST_DEFAULT_DURATION = 5_000 // 5 segundos

const VideochamadaToastContext = createContext<VideochamadaToastContextValue | null>(null)

export function VideochamadaToastProvider(props: Readonly<VideochamadaToastProviderProps>) {
  const { children } = props
  const [toasts, setToasts] = useState<VideochamadaToastModel[]>([])

  const show = useCallback((options: ShowToastOptions) => {
    const { body, position = 'bottom-right', duration = TOAST_DEFAULT_DURATION } = options
    const id = uuidv4()

    setToasts((prev) => [
      ...prev,
      {
        id,
        body,
        position,
        duration,
      },
    ])

    return id
  }, [])

  const clear = useCallback((position?: VideochamadaToastPosition) => {
    setToasts((prev) =>
      prev.map((toast) => (!position || toast.position === position ? { ...toast, dismissing: true } : toast))
    )
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const value = useMemo(() => ({ toasts, show, clear, dismiss }), [toasts, show, clear, dismiss])

  return <VideochamadaToastContext.Provider value={value}>{children}</VideochamadaToastContext.Provider>
}

export function useVideochamadaToast() {
  const context = useContext(VideochamadaToastContext)

  if (!context) {
    throw new Error('useVideochamadaToast deve ser usado dentro de um VideochamadaToastProvider')
  }

  return context
}
